import type {
  DownloadInlineBlobInput,
  DownloadInlinePdfInput,
  DownloadOutcome,
  PdfDownloadService,
} from "./PdfDownloadService.js";

/**
 * Phase 13h — in-memory `PdfDownloadService` for tests.
 *
 * Records every `openPresignedUrl` call (URL → `openedUrls`) and every
 * `downloadInlinePdf` call (filename + decoded byte length →
 * `downloadedFiles`) so tests can assert the consumer fed the right
 * artifact to the seam without touching the DOM.
 *
 * Mirrors the FIFO-queue pattern Phase 13e / 13f / 13h's other in-
 * memory adapters established: the default outcome is `{ kind: "ok" }`;
 * pre-enqueue a failure outcome to exercise the error arm.
 */

export type InMemoryPdfDownloadOptions = {
  readonly outcomes?: ReadonlyArray<DownloadOutcome>;
};

export type RecordedInlineDownload = {
  readonly filename: string;
  readonly byteLength: number;
  /**
   * Phase 14a — content type the consumer requested. The PDF arm
   * stamps `"application/pdf"`; the inline-blob arm passes through
   * whatever the caller specified.
   */
  readonly contentType: string;
};

export class InMemoryPdfDownloadService implements PdfDownloadService {
  private readonly queue: DownloadOutcome[];
  private readonly _openedUrls: string[] = [];
  private readonly _downloadedFiles: RecordedInlineDownload[] = [];

  constructor(options: InMemoryPdfDownloadOptions = {}) {
    this.queue = [...(options.outcomes ?? [])];
  }

  enqueue(outcome: DownloadOutcome): void {
    this.queue.push(outcome);
  }

  async openPresignedUrl(url: string): Promise<DownloadOutcome> {
    this._openedUrls.push(url);
    return this.dequeue();
  }

  async downloadInlinePdf(
    input: DownloadInlinePdfInput,
  ): Promise<DownloadOutcome> {
    // base64-decode just for the length — tests assert against
    // (filename, byteLength) without re-decoding the payload.
    let byteLength = 0;
    try {
      byteLength = decodeBase64Length(input.pdfBase64);
    } catch {
      byteLength = -1;
    }
    this._downloadedFiles.push({
      filename: input.filename,
      byteLength,
      contentType: "application/pdf",
    });
    return this.dequeue();
  }

  async downloadInlineBlob(
    input: DownloadInlineBlobInput,
  ): Promise<DownloadOutcome> {
    let byteLength = 0;
    if (input.payload.kind === "base64") {
      try {
        byteLength = decodeBase64Length(input.payload.base64);
      } catch {
        byteLength = -1;
      }
    } else {
      // UTF-8 byte length is the encoder's output length. Stub via a
      // direct calculation rather than instantiating `TextEncoder`
      // so the in-memory adapter stays free of Web Platform globals.
      byteLength = utf8ByteLength(input.payload.text);
    }
    this._downloadedFiles.push({
      filename: input.filename,
      byteLength,
      contentType: input.contentType,
    });
    return this.dequeue();
  }

  private dequeue(): DownloadOutcome {
    const next = this.queue.shift();
    if (next === undefined) {
      return { kind: "ok" };
    }
    return next;
  }

  get openedUrls(): ReadonlyArray<string> {
    return this._openedUrls;
  }

  get downloadedFiles(): ReadonlyArray<RecordedInlineDownload> {
    return this._downloadedFiles;
  }
}

const decodeBase64Length = (b64: string): number => {
  // Approximate length without allocating the full buffer in tests.
  // Equals atob(b64).length for canonical encodings.
  let padding = 0;
  if (b64.endsWith("==")) padding = 2;
  else if (b64.endsWith("=")) padding = 1;
  return Math.max(0, Math.floor((b64.length / 4) * 3) - padding);
};

/**
 * Pure UTF-8 byte-length counter — matches the standard encoder's
 * output for any input string without invoking `TextEncoder` (the
 * in-memory adapter stays free of browser globals).
 */
const utf8ByteLength = (text: string): number => {
  let n = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) n += 1;
    else if (code < 0x800) n += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      // High surrogate — paired with the next low surrogate produces
      // a 4-byte UTF-8 sequence. Advance past the low surrogate.
      n += 4;
      i++;
    } else n += 3;
  }
  return n;
};
